<template>
  <div id="app">
    <Header />
    <div class="header-margin"></div>
    <UpgradePackagePanel />
    <ConnectionAlertsPanel />
    <ExplanationPopup />
    <SearchFloatButton v-if="isUserSuperadmin" />
    <router-view v-if="!isLoading" class="view"></router-view>
  </div>
</template>

<script lang="ts">
import * as Sentry from "@sentry/vue";
import VueMeta from 'vue-meta';
import { Component, Vue, Watch } from "vue-property-decorator";
import { Action, Getter } from "vuex-class";
import { setDocumentDirectionPerLocale, setDocumentLang } from "@/services/I18n";
import ExplanationPopup from "./components/ExplanationPopup.vue";
import SearchFloatButton from "./components/SearchFloatButton.vue";
import ConnectionAlertsPanel from "./components/ConnectionAlertsPanel.vue";
import UpgradePackagePanel from "./components/UpgradePackagePanel.vue";
import Header from "./components/Header/Header.vue";
import { Meta } from "./decorators/MetaDecorator";
import { LeadsPageKind } from './models/LeadsPageKind';
import { UserData } from "./models/UserData";
import ApiService from "./services/api/ApiService";
import CampaignService from './services/CampaignService';
import ConfigService from "./services/ConfigService";
import HelperService from "./services/HelperService";
import LeadService from './services/LeadService';
import PhoneService from './services/PhoneService';

Vue.use(VueMeta);
const namespace = "mainModule";

@Component({
  components: {
    ConnectionAlertsPanel,
    ExplanationPopup,
    Header,
    SearchFloatButton,
    UpgradePackagePanel
  },
})

export default class App extends Vue {

  @Meta
  myMetaConfig() {
    return {
      title: "Default Title",
      titleTemplate: "%s | FixDigital"
    };
  }

  //#region Vuex getters and actions

  @Getter("userData", { namespace }) userData!: UserData;
  @Action("getUserData", { namespace }) getUserData!: () => Promise<UserData>;
  @Action("setUserData", { namespace }) setUserData!: (userData: UserData | null) => void;

  //#endregion Vuex getters and actions


  //#region Properties

  isLoading = false;

  //#endregion Properties


  //#region Watchers

  @Watch("$i18n.locale", { immediate: true })
  localeChanged(newLocale: string, oldLocale: string): void {
    if (newLocale === oldLocale) {
      return;
    }
    setDocumentLang(newLocale);
    setDocumentDirectionPerLocale(newLocale);
  }

  //#endregion Watchers


  //#region Getters

  get isUserSuperadmin(): boolean {
    return HelperService.isUserInRole(this.userData, ConfigService.superAdminUserRole);
  }

  get isUserCheckList(): boolean {
    return HelperService.isUserInRole(this.userData, ConfigService.checklistInProcessUserRole);
  }

  //#endregion Getters


  //#region Lifecycle

  created() {
    let loader = this.$loading.show();
    this.isLoading = true;
    const cachedUserDataId: number | undefined = this.userData?.Id;
    this.getUserData().then((userData: UserData) => {
      if (userData === undefined) {
        return;
      }
      if (!cachedUserDataId || cachedUserDataId !== userData.Id) {
        CampaignService.resetFilters(false);
        LeadService.resetFilters(LeadsPageKind.Clients, false);
        LeadService.resetFilters(LeadsPageKind.Leads, false);
        PhoneService.resetFilters(false);
      }
      Sentry.configureScope(scope => {
        scope.setUser({
          id: userData.Id?.toString(),
          email: userData.Email,
          username: userData.Name,
        });
        scope.setTag("apptype", "vuefront");
      });
      // fix for case when you work in vue page, go to asp page, switch language and go back to vue page
      if (userData.CurrentCulture && userData.CurrentCulture !== this.$i18n.locale) {
        this.$i18n.locale = userData.CurrentCulture;
        localStorage.locale = userData.CurrentCulture;
        location.reload();
      }
      this.isLoading = false;
      if(this.isUserCheckList){
        location.href="/GetStarted/Checklist";
      }
      loader.hide();
    });
  }

  mounted() {
    // sync with changing language on old mvc pages
    if (localStorage.newLocale !== undefined) {
      this.setUserData(null);
      this.$i18n.locale = localStorage.newLocale;
      ApiService.setLanguage(localStorage.newLocale);
      localStorage.removeItem("newLocale");
    }
  }

  //#endregion Lifecycle
}
</script>

<style lang="scss">
@import "~bulma/sass/utilities/_all";

$dark: #263238;
$dark-light: findLightColor($dark);
$dark-dark: findDarkColor($dark);
$dark-invert: findColorInvert($dark);

$primary: #0093d0;
$primary-light: findLightColor($primary);
$primary-lighter: #30c3ff;
$primary-dark: findDarkColor($primary);
$primary-invert: findColorInvert($primary);

$success: #269339;
$success-light: findLightColor($success);
$success-dark: findDarkColor($success);
$success-invert: findColorInvert($success);

$danger: #de473c;
$danger-light: findLightColor($danger);
$danger-dark: findDarkColor($danger);
$danger-invert: findColorInvert($danger);

$warning: #ff9724;
$warning-light: findLightColor($warning);
$warning-dark: findDarkColor($warning);
$warning-invert: findColorInvert($warning);

$info: #66bee3;
$info-light: findLightColor($info);
$info-dark: findDarkColor($info);
$info-invert: findColorInvert($info);

$link: #0f69c9;
$link-light: findLightColor($link);
$link-dark: findDarkColor($link);
$link-invert: findColorInvert($link);

$dark4: #435c65;
$dark4-invert: findColorInvert($dark4);

$disabled: #ccc;

// Lists and maps
$custom-colors: null !default;
$custom-shades: null !default;

// Setup $colors to use as bulma classes (e.g. 'is-twitter')
$colors: mergeColorMaps(
        (
            "white": (
                $white,
                $black,
            ),
            "black": (
                $black,
                $white,
            ),
            "light": (
                $light,
                $light-invert,
            ),
            "dark": (
                $dark,
                $dark-invert,
            ),
            "primary": (
                $primary,
                $primary-invert,
                $primary-light,
                $primary-dark,
            ),
            "link": (
                $link,
                $link-invert,
                $link-light,
                $link-dark,
            ),
            "info": (
                $info,
                $info-invert,
                $info-light,
                $info-dark,
            ),
            "success": (
                $success,
                $success-invert,
                $success-light,
                $success-dark,
            ),
            "warning": (
                $warning,
                $warning-invert,
                $warning-light,
                $warning-dark,
            ),
            "danger": (
                $danger,
                $danger-invert,
                $danger-light,
                $danger-dark,
            ),
            "dark4": (
                $dark4,
                $dark4-invert,
            ),
        ),
        $custom-colors
);

@import "~bulma";
@import "~buefy/src/scss/buefy";

@import "@/assets/styles/default";
@import "@/assets/styles/overrides";
@import "@/assets/styles/common";
@import "@/assets/styles/connection-alerts";

.header-margin {
  height: 152px;
}
</style>
